import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { TOKEN_KEY, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [ready, setReady] = useState(false);
  const [membershipConfig, setMembershipConfig] = useState(null);

  useEffect(() => {
    api.get("/membership/config").then(({ data }) => setMembershipConfig(data)).catch(() => {});
  }, []);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const requestOtp = async (email) => {
    try {
      const { data } = await api.post("/auth/request-otp", { email });
      return { ok: true, isNew: data.is_new, resendAfter: data.resend_after, expiresIn: data.expires_in };
    } catch (e) {
      return { ok: false, error: apiError(e.response?.data?.detail, "Could not send the code") };
    }
  };

  const verifyOtp = async ({ email, code, name, reg_no, remember = true }) => {
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code, name, reg_no, remember });
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e.response?.data?.detail, "Verification failed") };
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch { /* ignore */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, ready, membershipConfig, requestOtp, verifyOtp, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
